import React, { useEffect, useRef } from "react";
import { X, Trash2 } from "lucide-react";

/**
 * V1.9.2 — Opening Type Editor (Door + Window).
 *
 * Click any door/window on the 2D plan → tiny floating popover that lets
 * the user swap its `type` and delete it. Pure presentational — the
 * parent owns the actual mutation via the `onChange(newType)` and
 * `onDelete()` callbacks.
 *
 * Anchored next to the click coordinate `{x, y}` in DOM-space.
 */
const DOOR_TYPES = [
  { id: "swing",         label: "Swing" },
  { id: "single",        label: "Single Entry" },
  { id: "french",        label: "French" },
  { id: "dutch",         label: "Dutch" },
  { id: "sliding",       label: "Sliding" },
  { id: "sliding_glass", label: "Glass Slider" },
  { id: "pocket",        label: "Pocket" },
  { id: "bifold",        label: "Bifold" },
];
const WINDOW_TYPES = [
  { id: "fixed",       label: "Fixed" },
  { id: "single_hung", label: "Single-Hung" },
  { id: "double_hung", label: "Double-Hung" },
  { id: "sliding",     label: "Sliding" },
  { id: "casement",    label: "Casement" },
  { id: "awning",      label: "Awning" },
  { id: "hopper",      label: "Hopper" },
  { id: "bay",         label: "Bay" },
];
// V2.0.1 — door types that have a hinged swing arc; the editor exposes
// inswing/outswing + left/right hinge toggles for these.
const HINGED_TYPES = new Set(["swing", "single", "french", "dutch"]);

export default function OpeningEditor({
  kind, // "door" | "window"
  currentType,
  currentHinge,        // "auto" | "left" | "right"
  currentSwingDir,     // "auto" | "inswing" | "outswing"
  anchorX,
  anchorY,
  onChange,
  onHingeChange,
  onSwingDirChange,
  onDelete,
  onClose,
}) {
  const ref = useRef(null);
  const options = kind === "door" ? DOOR_TYPES : WINDOW_TYPES;
  const isHinged = kind === "door" && HINGED_TYPES.has(currentType);
  const hinge = currentHinge || "auto";
  const swingDir = currentSwingDir || "auto";

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  // V2.0.2 — Smart vertical placement. Estimate the popover height
  // (4 chip rows + optional hinge row + delete) and flip ABOVE the
  // anchor when the click sits low enough that growing DOWN would put
  // the Delete button off-screen. Previously the popover always
  // dropped below `anchorY - 8`, which meant openings along the FRONT
  // wall (bottom of the plan) lost the Delete row.
  const vw = typeof window !== "undefined" ? window.innerWidth : 1920;
  const vh = typeof window !== "undefined" ? window.innerHeight : 1080;
  const estHeight = isHinged ? 300 : 180;
  const left = Math.min(anchorX, vw - 260);
  const wouldOverflow = anchorY + estHeight + 8 > vh;
  const top = wouldOverflow
    ? Math.max(8, anchorY - estHeight - 8)
    : Math.max(8, anchorY - 8);

  return (
    <div
      ref={ref}
      data-testid={`opening-editor-${kind}`}
      style={{ position: "fixed", left, top, zIndex: 50 }}
      className="w-60 border border-brass-500 bg-zinc-950 shadow-[0_12px_36px_rgba(0,0,0,0.6)]"
    >
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-zinc-800">
        <div className="font-mono text-[9px] uppercase tracking-widest text-brass-400">
          // {kind} TYPE
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="opening-editor-close"
          className="text-zinc-400 hover:text-zinc-100"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <div className="p-1.5 grid grid-cols-2 gap-1">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            data-testid={`opening-type-${o.id}`}
            onClick={() => onChange(o.id)}
            aria-pressed={currentType === o.id}
            className={
              "px-2 py-1 border font-mono text-[10px] uppercase tracking-widest text-left transition-colors " +
              (currentType === o.id
                ? "border-brass-500 bg-brass-950 text-brass-100"
                : "border-zinc-700 text-zinc-300 hover:border-zinc-500")
            }
          >
            {o.label}
          </button>
        ))}
      </div>
      {/* V2.0.1 — Hinged-door specifics. Visible only for swing-style
          doors (swing/single/french/dutch) where the swing arc + hinge
          end actually matter. */}
      {isHinged && onSwingDirChange && onHingeChange && (
        <div
          data-testid="opening-editor-hinge-row"
          className="px-1.5 pb-1.5 space-y-1.5 border-t border-zinc-800 pt-1.5"
        >
          <div>
            <div className="font-mono text-[8px] uppercase tracking-widest text-zinc-500 mb-0.5">
              // Swing
            </div>
            <div className="inline-flex w-full border border-zinc-700">
              {[
                { id: "auto",     label: "Auto" },
                { id: "inswing",  label: "Inswing" },
                { id: "outswing", label: "Outswing" },
              ].map((opt, i) => (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`opening-swing-${opt.id}`}
                  onClick={() => onSwingDirChange(opt.id)}
                  aria-pressed={swingDir === opt.id}
                  className={
                    "flex-1 px-1.5 py-1 font-mono text-[9px] uppercase tracking-widest transition-colors " +
                    (i > 0 ? "border-l border-zinc-700 " : "") +
                    (swingDir === opt.id
                      ? "bg-brass-950 text-brass-100"
                      : "text-zinc-400 hover:bg-zinc-900")
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="font-mono text-[8px] uppercase tracking-widest text-zinc-500 mb-0.5">
              // Hinge
            </div>
            <div className="inline-flex w-full border border-zinc-700">
              {[
                { id: "auto",  label: "Auto" },
                { id: "left",  label: "Left" },
                { id: "right", label: "Right" },
              ].map((opt, i) => (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`opening-hinge-${opt.id}`}
                  onClick={() => onHingeChange(opt.id)}
                  aria-pressed={hinge === opt.id}
                  className={
                    "flex-1 px-1.5 py-1 font-mono text-[9px] uppercase tracking-widest transition-colors " +
                    (i > 0 ? "border-l border-zinc-700 " : "") +
                    (hinge === opt.id
                      ? "bg-brass-950 text-brass-100"
                      : "text-zinc-400 hover:bg-zinc-900")
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="px-1.5 pb-1.5">
        <button
          type="button"
          onClick={onDelete}
          data-testid="opening-editor-delete"
          className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 border border-orange-700 hover:bg-orange-950/40 text-orange-300 font-mono text-[10px] uppercase tracking-widest"
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>
    </div>
  );
}
